import { useMemo, useState } from "react";
import { Minus, Plus, Search } from "lucide-react";
import {
	EMAIL_INVALID_MSG,
	isValidEmail,
	type FieldErrors,
} from "@shared/validators";
import PageCard from "@/components/ui/PageCard";
import { Button } from "@/components/ui/Button";
import { ContactInfoFields } from "@/components/ui/ContactInfoFields";
import { DateInputNZ } from "@/components/ui/DateInputNZ";
import { FieldError } from "@/components/ui/FieldError";
import { track } from "@/lib/analytics";
import { InquirySuccess } from "./InquirySuccess";
import { StepFooter } from "./StepFooter";
import { buildDryHireInquiryFormData, type DryHireDraft } from "./submit-inquiry";
import { useInquirySubmit } from "./useInquirySubmit";
import {
	useEquipmentCatalogue,
	type CatalogueItem,
} from "./equipment-catalogue";
import type { InquiryProfileConfig } from "./inquiry-profile-schema";
import step from "./step.module.css";
import styles from "./DryHireForm.module.css";

/** Mirrors the backend caps (maxDryHireLines / maxDryHireQty). */
export const MAX_DRY_HIRE_LINES = 50;
export const MAX_DRY_HIRE_QTY = 99;

interface DryHireFormProps {
	profile: InquiryProfileConfig;
	draft: DryHireDraft;
	onDraftChange: (draft: DryHireDraft) => void;
	onBack: () => void;
}

type DryHireField =
	| "lines"
	| "startDate"
	| "endDate"
	| "contactName"
	| "contactEmail";

/** Pure validator, exported for table-driven tests. Kept hand-rolled: the
 *  line/date rules are cross-field, not per-field specs. */
export function validateDryHire(draft: DryHireDraft): FieldErrors<DryHireField> {
	const errors: FieldErrors<DryHireField> = {};
	if (draft.lines.length === 0) {
		errors.lines = "Select at least one item to hire";
	} else if (draft.lines.length > MAX_DRY_HIRE_LINES) {
		errors.lines = "Too many items selected";
	} else if (
		draft.lines.some(
			(line) => line.quantity < 1 || line.quantity > MAX_DRY_HIRE_QTY,
		)
	) {
		errors.lines = `Item quantities must be between 1 and ${MAX_DRY_HIRE_QTY}`;
	}
	if (!draft.startDate) {
		errors.startDate = "Choose a start date";
	}
	if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) {
		errors.endDate = "End date must be on or after the start date";
	}
	if (!draft.contactName.trim()) errors.contactName = "Enter your name";
	if (!isValidEmail(draft.contactEmail.trim())) {
		errors.contactEmail = EMAIL_INVALID_MSG;
	}
	return errors;
}

const nzd = new Intl.NumberFormat("en-NZ", {
	style: "currency",
	currency: "NZD",
});

function CatalogueRow({
	item,
	quantity,
	onQuantityChange,
}: {
	item: CatalogueItem;
	quantity: number;
	onQuantityChange: (next: number) => void;
}): React.JSX.Element {
	return (
		<div className={styles.row}>
			{item.imageUrl && (
				<img
					src={item.imageUrl}
					alt=""
					className={styles.thumb}
				/>
			)}
			<div className={styles.rowText}>
				<span className={styles.itemName}>
					{item.name}
				</span>
				<span className={styles.itemRate}>
					{nzd.format(item.hireRatePerDay)}/day
				</span>
			</div>
			<div className={styles.stepper}>
				<Button
					variant="secondary"
					size="icon"
					aria-label={`Remove one ${item.name}`}
					disabled={quantity === 0}
					onClick={() => onQuantityChange(Math.max(0, quantity - 1))}
				>
					<Minus className={styles.stepperIcon} />
				</Button>
				<span className={styles.quantity}>
					{quantity}
				</span>
				<Button
					variant="secondary"
					size="icon"
					aria-label={`Add one ${item.name}`}
					disabled={quantity >= MAX_DRY_HIRE_QTY}
					onClick={() =>
						onQuantityChange(Math.min(MAX_DRY_HIRE_QTY, quantity + 1))
					}
				>
					<Plus className={styles.stepperIcon} />
				</Button>
			</div>
		</div>
	);
}

export function DryHireForm({
	profile,
	draft,
	onDraftChange,
	onBack,
}: DryHireFormProps): React.JSX.Element {
	const catalogue = useEquipmentCatalogue();
	const [search, setSearch] = useState("");
	const [errors, setErrors] = useState<FieldErrors<DryHireField>>({});
	const lifecycle = useInquirySubmit({
		profileKey: profile.key,
		path: "dry_hire",
		build: (submissionKey) =>
			buildDryHireInquiryFormData({ profile, draft, submissionKey }),
	});

	const quantityByID = useMemo(() => {
		const map = new Map<string, number>();
		for (const line of draft.lines) map.set(line.equipmentId, line.quantity);
		return map;
	}, [draft.lines]);

	const visibleItems = useMemo(() => {
		if (catalogue.status !== "success") return [];
		const needle = search.trim().toLowerCase();
		if (!needle) return catalogue.items;
		return catalogue.items.filter((item) =>
			item.name.toLowerCase().includes(needle),
		);
	}, [catalogue, search]);

	const update = <K extends keyof DryHireDraft>(
		field: K,
		value: DryHireDraft[K],
	): void => {
		onDraftChange({ ...draft, [field]: value });
		setErrors((current) => ({ ...current, [field]: undefined }));
	};

	const setQuantity = (equipmentId: string, quantity: number): void => {
		const others = draft.lines.filter(
			(line) => line.equipmentId !== equipmentId,
		);
		const lines =
			quantity > 0 ? [...others, { equipmentId, quantity }] : others;
		onDraftChange({ ...draft, lines });
		setErrors((current) => ({ ...current, lines: undefined }));
	};

	const selectedSummary = useMemo(() => {
		if (catalogue.status !== "success" || draft.lines.length === 0) return "";
		const nameByID = new Map(
			catalogue.items.map((item) => [item.id, item.name]),
		);
		return draft.lines
			.map(
				(line) =>
					`${line.quantity}× ${nameByID.get(line.equipmentId) ?? "Item"}`,
			)
			.join(", ");
	}, [catalogue, draft.lines]);

	const submit = (): void => {
		const nextErrors = validateDryHire(draft);
		setErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) {
			track("field_error_shown", {
				profileKey: profile.key,
				path: "dry_hire",
				fieldNames: Object.keys(nextErrors),
			});
			return;
		}
		void lifecycle.submit();
	};

	if (lifecycle.state.status === "success") {
		return (
			<InquirySuccess
				profile={profile}
				path="dry_hire"
				statusToken={lifecycle.state.statusToken}
				title="Hire request received"
				staticSubtitle="We'll confirm availability and follow up by email."
			/>
		);
	}

	return (
		<PageCard>
			<div className={styles.body}>
				<h1 className={step.heading}>
					Hire equipment
				</h1>
				<p className={styles.intro}>
					Choose the gear you need and pick your dates. We&apos;ll confirm
					availability and send a quote.
				</p>

				<div className={styles.fields}>
					<div>
						<span className={styles.label}>
							Equipment
						</span>
						{catalogue.status === "loading" && (
							<p className={styles.muted}>Loading equipment…</p>
						)}
						{catalogue.status === "error" && (
							<div className={styles.errorRow}>
								<p role="alert" className={styles.submitError}>
									We couldn&apos;t load the equipment list.
								</p>
								<Button
									variant="secondary"
									size="sm"
									onClick={catalogue.retry}
								>
									Try again
								</Button>
							</div>
						)}
						{catalogue.status === "success" && (
							<>
								<div className={styles.searchWrapper}>
									<Search className={styles.searchIcon} />
									<input
										type="search"
										value={search}
										onChange={(event) => setSearch(event.target.value)}
										placeholder="Search equipment"
										aria-label="Search equipment"
										className={styles.search}
									/>
								</div>
								<div className={styles.list}>
									{visibleItems.length === 0 && (
										<p className={styles.empty}>
											No equipment matches your search.
										</p>
									)}
									{visibleItems.map((item) => (
										<CatalogueRow
											key={item.id}
											item={item}
											quantity={quantityByID.get(item.id) ?? 0}
											onQuantityChange={(next) => setQuantity(item.id, next)}
										/>
									))}
								</div>
							</>
						)}
						{selectedSummary && (
							<p className={styles.note}>
								Selected: {selectedSummary}
							</p>
						)}
						<FieldError message={errors.lines} />
					</div>

					<div className={styles.contactPair}>
						<label className={styles.field}>
							<span className={styles.label}>
								Hire start date
							</span>
							<DateInputNZ
								value={draft.startDate}
								onChange={(iso) => update("startDate", iso)}
							/>
							<FieldError message={errors.startDate} />
						</label>
						<label className={styles.field}>
							<span className={styles.label}>
								Hire end date{" "}
								<span className={styles.optional}>(optional)</span>
							</span>
							<DateInputNZ
								value={draft.endDate}
								onChange={(iso) => update("endDate", iso)}
							/>
							<FieldError message={errors.endDate} />
						</label>
					</div>

					<ContactInfoFields
						contactName={draft.contactName}
						contactEmail={draft.contactEmail}
						contactPhone={draft.contactPhone}
						showValidation
						labels={{ name: "Your name", email: "Email address", phone: "Phone" }}
						nameError={errors.contactName}
						emailError={errors.contactEmail}
						onNameChange={(value) => update("contactName", value)}
						onEmailChange={(value) => update("contactEmail", value)}
						onPhoneChange={(value) => update("contactPhone", value)}
						phoneOptional
					/>

					{lifecycle.submitError && (
						<p role="alert" className={styles.submitError}>
							{lifecycle.submitError}
						</p>
					)}
				</div>
				<StepFooter
					onBack={onBack}
					backLabel="Back to options"
					onContinue={submit}
					continueLabel="Request hire"
					loading={lifecycle.isSubmitting}
				/>
			</div>
		</PageCard>
	);
}
